import { useState } from 'react';
import ThreeDotLoading from 'components/ui/ThreeDotLoading';
import Icon from '../../../components/AppIcon';

const CheckInOutModal = ({ isOpen, onClose, onConfirm, type, isSubmitting }) => {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const reasonOptions = [
    { value: 'lunch-break', label: 'Lunch Break' },
    { value: 'day-end', label: 'Day End' },
    { value: 'other', label: 'Other' },
  ];

  const handleReasonChange = (value) => {
    setReason(value);
    setShowCustom(value === 'other');
    if (value !== 'other') {
      setCustomReason('');
    }
  };

  const handleConfirm = () => {
    const finalReason =
      reason === 'other'
        ? customReason?.trim()
        : reasonOptions?.find((opt) => opt?.value === reason)?.label || '';

    if (type === 'checkOut' && !finalReason) {
      alert('Please select or enter a reason for checking out.');
      return;
    }
    onConfirm(finalReason);
    setReason('');
    setCustomReason('');
    setShowCustom(false);
  };

  const handleClose = () => {
    setReason('');
    setCustomReason('');
    setShowCustom(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
          onClick={handleClose}
        ></div>

        {/* Modal panel */}
        <div className="relative inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-surface shadow-xl rounded-2xl border border-border min-h-[400px]">
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-text-primary">
                  {type === 'checkIn' ? 'Check In' : 'Check Out'}
                </h3>
                <p className="text-sm text-text-secondary mt-1">
                  {type === 'checkIn'
                    ? 'Ready to start your work day?'
                    : 'Please select a reason for checking out'}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-100 rounded-lg transition-colors duration-200"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {type === 'checkOut' && (
              <>
                {/* Reason selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-text-primary mb-3">
                    Reason for checking out
                  </label>
                  <div className="space-y-2">
                    {reasonOptions?.map((option) => (
                      <label
                        key={option?.value}
                        className="flex items-center space-x-3 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="reason"
                          value={option?.value}
                          checked={reason === option?.value}
                          onChange={(e) => handleReasonChange(e?.target?.value)}
                          className="w-4 h-4 text-primary border-border focus:ring-primary-500"
                        />
                        <span className="text-sm text-text-primary">{option?.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Custom reason input */}
                {showCustom && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Please specify your reason
                    </label>
                    <textarea
                      value={customReason}
                      onChange={(e) => setCustomReason(e?.target?.value)}
                      placeholder="Enter your reason..."
                      rows="3"
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 resize-none"
                    />
                  </div>
                )}
              </>
            )}

            {/* Current time display */}
            <div className="mb-6 p-3 bg-secondary-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} color="var(--color-text-secondary)" />
                <span className="text-sm text-text-secondary">Current time:</span>
                <span className="text-sm font-medium text-text-primary">
                  {new Date()?.toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true,
                  })}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex items-center space-x-2 mt-auto">
              <button
                onClick={handleClose}
                className="flex-1 px-2 py-2 text-text-secondary bg-secondary-100 hover:bg-secondary-200 rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 ${
                  type === 'checkIn'
                    ? 'bg-success hover:bg-success-600'
                    : 'bg-warning hover:bg-warning-600'
                }`}
              >
                {isSubmitting ? (
                  <ThreeDotLoading />
                ) : (
                  <>
                    <Icon name={type === 'checkIn' ? 'Play' : 'Square'} size={16} />
                    <span>{type === 'checkIn' ? 'Confirm Check In' : 'Confirm Check Out'}</span>
                  </>
                )}
              </button>
            </div>
          </>
        </div>
      </div>
    </div>
  );
};

export default CheckInOutModal;
